import { motion, useScroll, useTransform } from 'framer-motion'
import { useRef } from 'react'

const steps = [
  { 
    id: '01',
    label: 'Scout', 
    title: 'Deep Research', 
    description: 'We go deep. We analyse competitors, market gaps, and audience behaviour to find the opportunities that matter.' 
  },
  { 
    id: '02',
    label: 'Launch', 
    title: 'Precision Launch', 
    description: 'We move fast. We deploy campaigns and digital experiences built to perform — and get to market when it counts.' 
  },
  { 
    id: '03',
    label: 'Scale', 
    title: 'Sustained Growth', 
    description: 'We refine, iterate, and scale what works so you build momentum without burning out.' 
  },
  { 
    id: '04',
    label: 'Refine', 
    title: 'Adapt & Improve', 
    description: 'We keep evolving. Markets shift, and so do we. We continuously refine your strategy so you stay ahead.' 
  },
]

export default function Approach() {
  const containerRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  })

  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"])

  return (
    <section id="approach" className="py-32 bg-secondary relative" ref={containerRef}>
        {/* Background Texture */}
        <div className="absolute inset-0 bg-[url('/assets/bg-noise.png')] opacity-5 mix-blend-overlay pointer-events-none" />
        
        <div className="container relative z-10">
          <div className="flex flex-col md:flex-row gap-16 md:gap-32">
            
            {/* Sticky Header */}
            <div className="md:w-1/3 text-center md:text-left">
              <div className="sticky top-32">
                <h2 className="text-sm font-bold text-accent uppercase tracking-[0.2em] mb-6">Our Approach</h2>
                <h3 className="text-4xl md:text-6xl font-bold font-heading text-white leading-[0.95] uppercase mb-8">
                  From Insight <br/> <span className="text-accent">To Impact.</span>
                </h3>
                <p className="text-slate-400 text-lg leading-relaxed">
                  We don&apos;t hand over documents and hope for the best. We build executable strategies that move from research to launch to scale — and keep evolving.
                </p>
              </div>
            </div>

            {/* Timeline Steps */}
            <div className="md:w-2/3 relative">
              {/* Vertical Line */}
              <div className="absolute left-[19px] top-0 bottom-0 w-[2px] bg-white/10">
                <motion.div 
                  className="w-full bg-accent origin-top"
                  style={{ height: lineHeight }}
                />
              </div>

              <div className="flex flex-col gap-24">
                {steps.map((step, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.6, delay: i * 0.1 }}
                    className="relative pl-16 group"
                  >
                    {/* Dot */}
                    <div className="absolute left-[11px] top-2 w-[18px] h-[18px] rounded-full border-4 border-secondary bg-white/20 group-hover:bg-accent group-hover:scale-125 transition-all duration-300 z-10" />
                    
                    <span className="text-sm font-mono text-accent mb-2 block tracking-widest">{step.id} // {step.label}</span>
                    <h4 className="text-3xl md:text-4xl font-bold font-heading text-white mb-4 uppercase">{step.title}</h4>
                    <p className="text-slate-400 text-lg leading-relaxed max-w-xl group-hover:text-white transition-colors duration-300">
                      {step.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>

          </div>
        </div>
    </section>
  )
}
